import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import FluentIcon from './FluentIcon';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage, onPageChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [username, setUsername] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Fetch current user info
    const fetchUserInfo = async () => {
      try {
        const userInfo = await ApiService.getCurrentUser();
        setUsername(userInfo.username);
        setUserRole(userInfo.role);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };
    
    fetchUserInfo();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogout = () => {
    ApiService.logout();
    navigate('/login');
  };

  const isAdmin = userRole === 'admin';

  const navigation = [
    { name: 'Dashboard', id: 'dashboard', icon: 'clipboard-data', adminOnly: false },
    { name: 'Health Check', id: 'health', icon: 'heart-pulse', adminOnly: false },
    { name: 'Supported TLDs', id: 'tlds', icon: 'globe', adminOnly: false },
    { name: 'Bulk TLD Check', id: 'bulk-check', icon: 'checkmark-circle', adminOnly: true },
    { name: 'API Tokens', id: 'tokens', icon: 'shield-lock', adminOnly: true },
    { name: 'User Management', id: 'users', icon: 'person-accounts', adminOnly: true },
    { name: 'API Testing', id: 'testing', icon: 'beaker', adminOnly: false },
    { name: 'Help & Docs', id: 'help', icon: 'info', adminOnly: false },
  ].filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="layout-container">
      {/* Sidebar */}
      <div className={`sidebar ${isMobile ? 'sidebar-mobile' : ''} ${isMobile && !sidebarOpen ? 'sidebar-hidden' : ''}`}>
        <div className="sidebar-header">
                      <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
              >
                <circle 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  fill="url(#gradient1)" 
                  stroke="url(#gradient2)" 
                  strokeWidth="1"
                />
                <path 
                  d="M8 9h8M8 12h8M8 15h6" 
                  stroke="white" 
                  strokeWidth="1.5" 
                  strokeLinecap="round"
                />
                <circle 
                  cx="12" 
                  cy="12" 
                  r="2" 
                  fill="rgba(255,255,255,0.3)"
                />
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea" />
                    <stop offset="100%" stopColor="#764ba2" />
                  </linearGradient>
                  <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.2)" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            Domains.Land
          </div>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="sidebar-close-btn"
                          >
                <FluentIcon name="dismiss" size={16} />
              </button>
          )}
        </div>
        
        <nav className="sidebar-nav">
          <ul className="sidebar-nav-list">
            {navigation.map((item) => (
              <li key={item.id} className="sidebar-nav-item">
                <button
                  onClick={() => {
                    onPageChange(item.id);
                    if (isMobile) setSidebarOpen(false);
                    navigate(`/${item.id === 'dashboard' ? '' : item.id}`);
                  }}
                  className={`sidebar-nav-button ${currentPage === item.id ? 'active' : ''}`}
                >
                  <span className="sidebar-nav-icon">
                    <FluentIcon name={item.icon} size={18} />
                  </span>
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-footer-version">WHOIS API v2.0.0</div>
          <div className="sidebar-footer-credit">Powered by Domains.Land</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: 0 }}>
        {/* Top bar */}
        <header className="main-header">
          <div className="header-left">
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="mobile-menu-btn"
                              >
                <FluentIcon name="menu" size={18} />
                </button>
            )}
            <div>
              <h1 className="header-title">
                {navigation.find(item => item.id === currentPage)?.name || 'Dashboard'}
              </h1>
              <p className="header-subtitle">
                Professional WHOIS API Management Platform
              </p>
            </div>
          </div>
          
          <div className="header-right">
            <div className="api-status">
              <div className="api-status-indicator" />
              <span className="api-status-text">API Online</span>
            </div>

            <div className="user-profile">
              <div className="user-avatar">
                {username ? username.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-info">
                <div className="user-name">
                  {username || 'Admin'}
                </div>
                <div className="user-role">
                  {userRole === 'admin' ? 'Administrator' : 'User'}
                </div>
                </div>
                <button
                  onClick={handleLogout}
                className="logout-btn"
                title="Logout"
              >
                <FluentIcon name="sign-out" size={16} />
                </button>
              </div>
          </div>
        </header>

        {/* Content area */}
        <main className="main-content">
          {children}
        </main>
      </div>

      {/* Sidebar overlay for mobile */}
      {isMobile && sidebarOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 900,
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 